import { CallEffect, PutEffect } from 'redux-saga/effects';
import { call, put, SagaGenerator } from 'typed-redux-saga';
import { getData, postData, putData, deleteData } from '../shared/fetchApi';
import {
    checkSilosFailed,
    checkSilosSucceeded,
    fetchSiloFillLevelsFailed,
    fetchSiloFillLevelsSucceeded,
    fetchSiloHistoryFailed,
    fetchSiloHistorySucceeded,
    fetchSiloDeliveriesFailed,
    fetchSiloDeliveriesSucceeded,
    createSiloSucceeded,
    createSiloFailed,
    fetchSiloFillLevels,
    fetchSiloDetailsSucceeded,
    fetchSiloDetailsFailed,
    SiloDetailsDto,
    updateSiloSucceeded,
    updateSiloFailed,
    deleteSiloSucceeded,
    deleteSiloFailed,
    // deleteSilo,
} from '../store/Silo.store';
import type { SiloFillLevel, SiloHistory, SiloDeliveryRow, CreateSiloActionPayload, UpdateSiloActionPayload } from '../store/Silo.store';
import { updateHasSilos } from '../store/Navigation.store';
import { PayloadAction } from '@reduxjs/toolkit';

export function* checkUserHasSilosWithFillLevelSaga(_action: PayloadAction): SagaGenerator<void, PutEffect | CallEffect> {
    try {
        const hasSilos = (yield* call(getData, 'check/silos')) as boolean;
        yield* put(checkSilosSucceeded(hasSilos));
        yield* put(updateHasSilos(hasSilos));
    } catch (error: unknown) {
        console.error('Error checking silos:', error);
        yield* put(checkSilosFailed());
        yield* put(updateHasSilos(false));
    }
}

export function* fetchSiloFillLevelsSaga(): SagaGenerator<void, PutEffect | CallEffect> {
    try {
        const fillLevels = (yield* call(getData, 'silos/overview')) as SiloFillLevel[];
        yield* put(fetchSiloFillLevelsSucceeded(fillLevels));
    } catch (error: unknown) {
        console.error('Error fetching silo fill levels:', error);
        yield* put(fetchSiloFillLevelsFailed());
    }
}

export function* fetchSiloHistorySaga(action: PayloadAction): SagaGenerator<void, PutEffect | CallEffect> {
    try {
        const siloId = action.payload;
        const siloHistory = (yield* call(getData, 'silos/history?siloId=' + siloId)) as SiloHistory;
        console.log(siloHistory);
        yield* put(fetchSiloHistorySucceeded(siloHistory));
    } catch (error: unknown) {
        console.error('Error fetching silo history:', error);
        yield* put(fetchSiloHistoryFailed());
    }
}

export function* fetchSiloDeliveriesSaga(action: PayloadAction): SagaGenerator<void, PutEffect | CallEffect> {
    try {
        const siloId = action.payload;
        const raw = (yield* call(getData, `silos/${siloId}/order-statistics`)) as unknown as Array<{
            periodFrom: string;
            periodTo: string;
            articleNumber: string;
            articleName?: string;
            timesOrdered: number;
        }>;
        const deliveries: SiloDeliveryRow[] = (raw || []).map((r) => ({
            periodStart: r.periodFrom,
            periodEnd: r.periodTo,
            articleName: r.articleName ?? r.articleNumber,
            articleNumber: r.articleNumber,
            deliveriesCount: r.timesOrdered,
        }));
        yield* put(fetchSiloDeliveriesSucceeded(deliveries));
    } catch (error: unknown) {
        console.error('Error fetching silo deliveries:', error);
        yield* put(fetchSiloDeliveriesFailed());
    }
}

export function* createSiloSaga(action: PayloadAction<CreateSiloActionPayload>): SagaGenerator<void, PutEffect | CallEffect> {
    try {
        const payload = action.payload;
        yield* call(postData, 'silo', {
            nameOrNumber: payload.siloNumberOrName,
            customerNumber: payload.customerNumber,
            capacityTons: payload.capacityTons,
            initialFillLevel: payload.initialFillLevel,
            volumeM3: payload.volumeM3,
            consumptionEnabled: payload.consumptionEnabled,
            dailyConsumptionKg: payload.dailyConsumptionKg,
            warningsEnabled: payload.warningsEnabled,
            thresholdPercent: payload.thresholdPercent,
        });
        yield* put(createSiloSucceeded());
        yield* put(fetchSiloFillLevels());
        // Reload current page and show success banner via state (or navigate to overview if desired)
        if (action.payload.onSuccessNavigateToOverview) {
            // keep behavior: navigate to overview logic can live in a listener; for now reload to reflect changes
            window.location.reload();
        }
    } catch (error: unknown) {
        console.error('Error creating silo:', error);
        let message = 'Silo konnte nicht erstellt werden';

        if (error instanceof Error) {
            message = error.message;
            console.log('Error message from backend:', message);
            console.log('Error status:', (error as unknown as { status?: unknown }).status);
            console.log('Error statusText:', (error as unknown as { statusText?: unknown }).statusText);
            console.log('Error name:', error.name);
            console.log('Error stack:', error.stack);
        }

        yield* put(createSiloFailed(message));
    }
}

export function* fetchSiloDetailsSaga(action: PayloadAction<string>): SagaGenerator<void, PutEffect | CallEffect> {
    try {
        const siloId = action.payload;
        const raw = (yield* call(getData, `silos/${siloId}/details`)) as unknown as {
            siloName?: string;
            nameOrNumber?: string;
            customerNumber?: string;
            capacityTons?: number | null;
            volumeM3?: number | null;
            hasSensorData?: boolean;
            currentPercent?: number | null;
            lastUpdate?: string | null;
            consumptionEnabled?: boolean | null;
            dailyConsumptionKg?: number | null;
            warningsEnabled?: boolean | null;
            thresholdPercent?: number | null;
        };
        const normalized: SiloDetailsDto = {
            siloName: raw?.siloName ?? raw?.nameOrNumber ?? '',
            customerNumber: raw?.customerNumber ?? '',
            capacityTons: raw?.capacityTons ?? null,
            volumeM3: raw?.volumeM3 ?? null,
            hasSensorData: raw?.hasSensorData,
            currentPercent: raw?.currentPercent ?? null,
            lastUpdate: raw?.lastUpdate ?? null,
            consumptionEnabled: raw?.consumptionEnabled ?? null,
            dailyConsumptionKg: raw?.dailyConsumptionKg ?? null,
            warningsEnabled: raw?.warningsEnabled ?? null,
            thresholdPercent: raw?.thresholdPercent ?? null,
        };
        yield* put(fetchSiloDetailsSucceeded(normalized));
    } catch (error: unknown) {
        console.error('Error fetching silo details:', error);
        yield* put(fetchSiloDetailsFailed());
    }
}

export function* updateSiloSaga(action: PayloadAction<UpdateSiloActionPayload>): SagaGenerator<void, PutEffect | CallEffect> {
    try {
        const { siloId, siloName, initialFillTons, ...rest } = action.payload;
        const fillLevel =
            initialFillTons == null
                ? undefined
                : {
                      currentTons: initialFillTons,
                      timestamp: new Date().toISOString(),
                  };
        const backendPayload = {
            nameOrNumber: siloName,
            customerNumber: rest.customerNumber,
            capacityTons: rest.capacityTons,
            initialFillLevel: initialFillTons,
            fillLevel,
            volumeM3: rest.volumeM3,
            consumptionEnabled: rest.consumptionEnabled,
            dailyConsumptionKg: rest.dailyConsumptionKg,
            warningsEnabled: rest.warningsEnabled,
            thresholdPercent: rest.thresholdPercent,
        } as Record<string, unknown>;
        const updated = (yield* call(putData, `silos/${siloId}`, backendPayload)) as SiloDetailsDto;
        yield* put(updateSiloSucceeded(updated));
    } catch (error: unknown) {
        console.error('Error updating silo:', error);
        const message = error instanceof Error ? error.message : undefined;
        yield* put(updateSiloFailed(message));
    }
}

export function* deleteSiloSaga(action: PayloadAction<string>): SagaGenerator<void, PutEffect | CallEffect> {
    try {
        const siloId = action.payload;
        yield* call(deleteData, `silos/${siloId}`);
        yield* put(deleteSiloSucceeded());
        // refresh overview after delete
        yield* put(fetchSiloFillLevels());
    } catch (error: unknown) {
        const message = error instanceof Error ? error.message : undefined;
        yield* put(deleteSiloFailed(message));
    }
}
